import React from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { PanelContainer } from '../layout/PanelContainer';
import { InteractionConstraint } from '../../../ui/InteractionConstraint/InteractionConstraints';
import { useTheme } from '../../../context/ThemeContext';
import { Button } from '../layout/Button';
import type { RnaComplexProps } from '../../../App';
import { Context } from '../../../context/Context';
import { getSceneConstraintCounts, type CustomDomain } from '../../../utils/Visibility';

export interface SelectionPanelProps {
  constraint?: InteractionConstraint.Enum;
  onConstraintChange?: (constraint: InteractionConstraint.Enum) => void;
  rnaComplexProps?: RnaComplexProps;
}

const constraintGroups : Record<string, Array<InteractionConstraint.Enum>> = {
  'Primitives': [
    InteractionConstraint.Enum.SINGLE_NUCLEOTIDE,
    InteractionConstraint.Enum.SINGLE_BASE_PAIR,
  ],
  'Fragments': [
    InteractionConstraint.Enum.RNA_SINGLE_STRAND,
    InteractionConstraint.Enum.RNA_HELIX,
    InteractionConstraint.Enum.RNA_STACKED_HELIX,
    InteractionConstraint.Enum.RNA_CYCLE,
  ],
  'Units': [
    InteractionConstraint.Enum.RNA_SUB_DOMAIN,
    InteractionConstraint.Enum.RNA_MOLECULE,
    InteractionConstraint.Enum.RNA_COMPLEX,
    InteractionConstraint.Enum.SINGLE_COLOR,
    InteractionConstraint.Enum.ENTIRE_SCENE,
  ],
  'Scope': [],
};

const ConstraintChip: React.FC<{
  constraint: InteractionConstraint.Enum;
  isActive: boolean;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ constraint, isActive, onClick, disabled = false }) => {
  const { theme } = useTheme();
  const [isHovered, setIsHovered] = React.useState(false);

  const getConstraintIcon = (constraint: InteractionConstraint.Enum) => {
    // Use opposite theme's primary color to avoid conflicts with selection
    const iconPrimaryColor = theme.colors.primary;
    
    const iconMap: Partial<Record<InteractionConstraint.Enum, React.ReactNode>> = {
      [InteractionConstraint.Enum.SINGLE_NUCLEOTIDE]: (
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <circle cx="12" cy="12" r="9" fill={iconPrimaryColor} stroke="none" strokeWidth="1"/>
          <text x="12" y="12" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="bold" fill={theme.colors.textInverse} textAnchor="middle" dominantBaseline="central">N</text>
        </svg>
      ),
      [InteractionConstraint.Enum.SINGLE_BASE_PAIR]: (
        <svg width="48" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          {/* Left nucleotide */}
          <circle cx="0" cy="12" r="9" fill={iconPrimaryColor} stroke="none" strokeWidth="1"/>
          <text x="0" y="13" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="bold" fill={theme.colors.textInverse} textAnchor="middle" dominantBaseline="middle">C</text>
          
          {/* Bond line (solid for covalent) */}
          <line x1="9.5" y1="12" x2="20" y2="12" stroke={theme.colors.borderDark} strokeWidth="1.5"/>
          
          {/* Right nucleotide */}
          <circle cx="25" cy="12" r="9" fill={iconPrimaryColor} stroke="none" strokeWidth="1"/>
          <text x="25" y="13" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="bold" fill={theme.colors.textInverse} textAnchor="middle" dominantBaseline="middle">G</text>
        </svg>
      ),
      [InteractionConstraint.Enum.RNA_SINGLE_STRAND]: (
        <svg width="28" height="24" viewBox="0 0 64 48" xmlns="http://www.w3.org/2000/svg">
          {/* First nucleotide */}
          <circle cx="10" cy="28" r="9" fill={iconPrimaryColor} stroke={theme.colors.borderDark} strokeWidth="1"/>
          {/* <text x="10" y="28" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="bold" fill={theme.colors.textInverse} textAnchor="middle" dominantBaseline="central">N</text> */}
          
          {/* Curved bond */}
          <path d="M 18 26 Q 26 18 32 20" stroke={theme.colors.borderDark} strokeWidth="1.5" fill="none"/>
          
          {/* Second nucleotide */}
          <circle cx="32" cy="20" r="9" fill={iconPrimaryColor} stroke={theme.colors.borderDark} strokeWidth="1"/>
          {/* <text x="32" y="20" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="bold" fill={theme.colors.textInverse} textAnchor="middle" dominantBaseline="central">N</text> */}
          
          {/* Curved bond */}
          <path d="M 40 22 Q 46 26 54 28" stroke={theme.colors.borderDark} strokeWidth="1.5" fill="none"/>
          
          {/* Third nucleotide */}
          <circle cx="54" cy="28" r="9" fill={iconPrimaryColor} stroke={theme.colors.borderDark} strokeWidth="1"/>
          {/* <text x="54" y="28" fontFamily="Arial, sans-serif" fontSize="12" fontWeight="bold" fill={theme.colors.textInverse} textAnchor="middle" dominantBaseline="central">N</text> */}
        </svg>
      ),
      [InteractionConstraint.Enum.RNA_HELIX]: (
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          {/* Left strand */}
          <path d="M 6 4 Q 4 8 6 12 Q 8 16 6 20" stroke={iconPrimaryColor} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          
          {/* Right strand */}
          <path d="M 18 4 Q 20 8 18 12 Q 16 16 18 20" stroke={iconPrimaryColor} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          
          {/* Connecting rungs */}
          <line x1="6" y1="7" x2="18" y2="7" stroke={theme.colors.borderDark} strokeWidth="1.5"/>
          <line x1="6" y1="12" x2="18" y2="12" stroke={theme.colors.borderDark} strokeWidth="1.5"/>
          <line x1="6" y1="17" x2="18" y2="17" stroke={theme.colors.borderDark} strokeWidth="1.5"/>
        </svg>
      ),
      [InteractionConstraint.Enum.RNA_STACKED_HELIX]: (
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          {/* Top helix - left strand */}
          <path d="M 6 3 Q 4 5 6 7" stroke={iconPrimaryColor} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          
          {/* Top helix - right strand */}
          <path d="M 18 3 Q 20 5 18 7" stroke={iconPrimaryColor} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          
          {/* Top helix - rung */}
          <line x1="6" y1="5" x2="18" y2="5" stroke={theme.colors.borderDark} strokeWidth="1.5"/>
          
          {/* Middle helix - left strand */}
          <path d="M 6 10 Q 4 12 6 14" stroke={iconPrimaryColor} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          
          {/* Middle helix - right strand */}
          <path d="M 18 10 Q 20 12 18 14" stroke={iconPrimaryColor} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          
          {/* Middle helix - rung */}
          <line x1="6" y1="12" x2="18" y2="12" stroke={theme.colors.borderDark} strokeWidth="1.5"/>
          
          {/* Bottom helix - left strand */}
          <path d="M 6 17 Q 4 19 6 21" stroke={iconPrimaryColor} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          
          {/* Bottom helix - right strand */}
          <path d="M 18 17 Q 20 19 18 21" stroke={iconPrimaryColor} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          
          {/* Bottom helix - rung */}
          <line x1="6" y1="19" x2="18" y2="19" stroke={theme.colors.borderDark} strokeWidth="1.5"/>
        </svg>
      ),
      [InteractionConstraint.Enum.RNA_SUB_DOMAIN]: (
        <svg width="32" height="24" viewBox="0 0 28 24" xmlns="http://www.w3.org/2000/svg">
          {/* Main vertical stem - left */}
          <path d="M 12 22 L 12 8" stroke={iconPrimaryColor} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          
          {/* Main vertical stem - right */}
          <path d="M 16 22 L 16 8" stroke={iconPrimaryColor} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          
          {/* Vertical stem rungs */}
          <line x1="12" y1="20" x2="16" y2="20" stroke={theme.colors.borderDark} strokeWidth="1.5"/>
          <line x1="12" y1="17" x2="16" y2="17" stroke={theme.colors.borderDark} strokeWidth="1.5"/>
          <line x1="12" y1="14" x2="16" y2="14" stroke={theme.colors.borderDark} strokeWidth="1.5"/>
          <line x1="12" y1="11" x2="16" y2="11" stroke={theme.colors.borderDark} strokeWidth="1.5"/>
          
          {/* Top loop */}
          <path d="M 12 8 A 2 2 0 0 1 16 8" stroke={iconPrimaryColor} strokeWidth="2.5" fill="none" strokeLinecap="round"/>
          
          {/* Left horizontal branch */}
          <path d="M 12 15 L 6 15 L 6 12" stroke={iconPrimaryColor} strokeWidth="2" fill="none" strokeLinecap="round"/>
          <path d="M 12 13 L 8 13 L 8 12" stroke={iconPrimaryColor} strokeWidth="2" fill="none" strokeLinecap="round"/>
          <line x1="6" y1="14" x2="8" y2="14" stroke={theme.colors.borderDark} strokeWidth="1"/>
          <line x1="6" y1="13" x2="8" y2="13" stroke={theme.colors.borderDark} strokeWidth="1"/>
          <path d="M 6 12 A 1 1 0 0 0 8 12" stroke={iconPrimaryColor} strokeWidth="2" fill="none"/>
          
          {/* Right horizontal branch */}
          <path d="M 16 15 L 22 15 L 22 12" stroke={iconPrimaryColor} strokeWidth="2" fill="none" strokeLinecap="round"/>
          <path d="M 16 13 L 20 13 L 20 12" stroke={iconPrimaryColor} strokeWidth="2" fill="none" strokeLinecap="round"/>
          <line x1="20" y1="14" x2="22" y2="14" stroke={theme.colors.borderDark} strokeWidth="1"/>
          <line x1="20" y1="13" x2="22" y2="13" stroke={theme.colors.borderDark} strokeWidth="1"/>
          <path d="M 20 12 A 1 1 0 0 1 22 12" stroke={iconPrimaryColor} strokeWidth="2" fill="none"/>
        </svg>
      ),
      [InteractionConstraint.Enum.RNA_CYCLE]: (
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <circle cx="12" cy="12" r="6" stroke={iconPrimaryColor} strokeWidth="2" fill="none"/>
          <circle cx="12" cy="12" r="7" stroke={theme.colors.borderDark} strokeWidth="0.5" fill="none"/>
          <circle cx="12" cy="12" r="5" stroke={theme.colors.borderDark} strokeWidth="0.5" fill="none"/>
        </svg>
      ),
      [InteractionConstraint.Enum.SINGLE_COLOR]: (
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <g transform="translate(12 12) scale(1.3) translate(-12 -12)">
            {/* Paint palette shape */}
            <path d="M 12 4 Q 6 4 4 8 Q 2 12 4 16 Q 6 20 12 20 Q 14 20 16 19 L 18 16 Q 20 14 18 12 Q 16 10 14 11 Q 12 12 12 10 Q 12 6 12 4 Z" 
              fill={iconPrimaryColor} 
              stroke="none" 
              strokeWidth="1.5"
            />
            
            {/* Paint wells - small circles */}
            <circle cx="8" cy="9.5" r="2" fill={theme.colorPalleteCircleFills[0]} stroke="none" strokeWidth="0.8"/>
            <circle cx="10" cy="14" r="2" fill={theme.colorPalleteCircleFills[1]} stroke="none" strokeWidth="0.8"/>
            <circle cx="14.5" cy="15" r="2" fill={theme.colorPalleteCircleFills[2]} stroke="none" strokeWidth="0.8"/>
          </g>
        </svg>
      ),
      [InteractionConstraint.Enum.RNA_MOLECULE]: (
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          {/* Continuous contour of folded RNA molecule */}
          <path
            d="M 4 20 
               Q 3 16 5 12 
               Q 7 8 12 6 
               Q 17 8 19 12 
               Q 21 16 20 20 
               Q 18 18 16 16 
               Q 14 14 12 14 
               Q 10 14 8 16 
               Q 6 18 4 20 Z" 
            stroke="none" 
            fill={iconPrimaryColor}
            strokeWidth="2.5" 
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      ),
      [InteractionConstraint.Enum.RNA_COMPLEX]: (
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <g
            transform = "translate(0, 2.5) scale(0.8)"
          >
            {/* First RNA molecule - primary color (top) */}
            <path
              d="M 4 12 
                Q 3 8 5 4 
                Q 7 0 12 -2 
                Q 17 0 19 4 
                Q 21 8 20 12 
                Q 18 10 16 8 
                Q 14 6 12 6 
                Q 10 6 8 8 
                Q 6 10 4 12 Z" 
              stroke="none" 
              fill={iconPrimaryColor}
              strokeWidth="2.5" 
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            
            {/* Second RNA molecule - accent color (bottom, fitting into groove) */}
            <path
              d="M 4 12
                Q 6 10 8 8
                Q 10 6 12 6
                Q 14 6 16 8
                Q 18 10 20 12
                Q 21 16 19 20
                Q 17 24 12 26
                Q 7 24 5 20
                Q 3 16 4 12 Z"
              stroke="none" 
              fill={theme.colors.accent} 
              strokeWidth="2.5" 
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </svg>
      ),
      [InteractionConstraint.Enum.ENTIRE_SCENE]: (
        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          {/* Canvas border */}
          <rect x="1" y="1" width="22" height="22" stroke={theme.colors.text} strokeWidth="1" fill="none" rx="1"/>
          
          {/* Small RNA molecule 1 - top left */}
          <path
            transform = "scale(0.5)"
            d="M 4 20 
               Q 3 16 5 12 
               Q 7 8 12 6 
               Q 17 8 19 12 
               Q 21 16 20 20 
               Q 18 18 16 16 
               Q 14 14 12 14 
               Q 10 14 8 16 
               Q 6 18 4 20 Z" 
            stroke="none" 
            fill={iconPrimaryColor}
            strokeWidth="2.5" 
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <g
            transform = "translate(16, 7) scale(0.5) translate(0, 3.5) rotate(45)"
          >
            {/* First RNA molecule - primary color (top) */}
            <path
              d="M 4 12 
                Q 3 8 5 4 
                Q 7 0 12 -2 
                Q 17 0 19 4 
                Q 21 8 20 12 
                Q 18 10 16 8 
                Q 14 6 12 6 
                Q 10 6 8 8 
                Q 6 10 4 12 Z" 
              stroke="none"
              fill={iconPrimaryColor}
              strokeWidth="2.5" 
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            
            {/* Second RNA molecule - accent color (bottom, fitting into groove) */}
            <path
              d="M 4 12
                Q 6 10 8 8
                Q 10 6 12 6
                Q 14 6 16 8
                Q 18 10 20 12
                Q 21 16 19 20
                Q 17 24 12 26
                Q 7 24 5 20
                Q 3 16 4 12 Z"
              stroke="none"
              fill={theme.colors.accent} 
              strokeWidth="2.5" 
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </g>
        </svg>
      ),
    };
    return iconMap[constraint] || (
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <circle cx="6" cy="6" r="2" stroke="currentColor" strokeWidth="1.2" fill="none"/>
      </svg>
    );
  };

  const getShortName = (constraint: InteractionConstraint.Enum) => {
    const nameMap: Partial<Record<InteractionConstraint.Enum, string>> = {
      [InteractionConstraint.Enum.SINGLE_NUCLEOTIDE]: 'Nucleotide',
      [InteractionConstraint.Enum.SINGLE_BASE_PAIR]: 'Base Pair',
      [InteractionConstraint.Enum.RNA_SINGLE_STRAND]: 'Strand',
      [InteractionConstraint.Enum.RNA_HELIX]: 'Helix',
      [InteractionConstraint.Enum.RNA_STACKED_HELIX]: 'Stacked',
      [InteractionConstraint.Enum.RNA_SUB_DOMAIN]: 'Domain',
      [InteractionConstraint.Enum.RNA_CYCLE]: 'Cycle',
      [InteractionConstraint.Enum.RNA_MOLECULE]: 'Molecule',
      [InteractionConstraint.Enum.RNA_COMPLEX]: 'Complex',
      [InteractionConstraint.Enum.SINGLE_COLOR]: 'Color',
      [InteractionConstraint.Enum.ENTIRE_SCENE]: 'Scene',
    };
    return nameMap[constraint] || constraint;
  };

  return (
    <Button label={getShortName(constraint)} onClick={onClick} disabled={disabled} variant={isActive ? 'simpleHighlight' : 'secondary'} icon={getConstraintIcon(constraint)}/>
  );
};

const CustomDomainsEditor: React.FC<{ rnaComplexProps: RnaComplexProps }> = ({ rnaComplexProps }) => {
  const { theme } = useTheme();
  const options = React.useContext(Context.App.InteractionConstraintOptions);
  const updateOptions = React.useContext(Context.App.UpdateInteractionConstraintOptions);
  const complexes = Object.entries(rnaComplexProps);
  const createDomain = React.useCallback((rnaComplexIndex?: number, rnaMoleculeName?: string) : CustomDomain | undefined => {
    const resolvedRnaComplexIndex = rnaComplexIndex ?? Number(complexes[0]?.[0]);
    const complex = rnaComplexProps[resolvedRnaComplexIndex];
    const resolvedRnaMoleculeName = rnaMoleculeName ?? Object.keys(complex?.rnaMoleculeProps ?? {})[0];
    const molecule = complex?.rnaMoleculeProps[resolvedRnaMoleculeName];
    if (molecule === undefined) return undefined;
    const displayed = Object.keys(molecule.nucleotideProps).map(Number).map(i => i + molecule.firstNucleotideIndex);
    if (displayed.length === 0) return undefined;
    return {
      rnaComplexIndex : resolvedRnaComplexIndex,
      rnaMoleculeName : resolvedRnaMoleculeName,
      start : Math.min(...displayed),
      stop : Math.max(...displayed)
    };
  }, [rnaComplexProps]);
  const update = (index: number, patch: Partial<CustomDomain>) => {
    const customDomains = options.customDomains.map((domain, i) => i === index ? { ...domain, ...patch } : domain);
    updateOptions({ customDomains });
  };
  const add = () => {
    const domain = createDomain();
    if (domain !== undefined) updateOptions({ customDomains: [...options.customDomains, domain] });
  };
  React.useEffect(() => {
    const validDomains = options.customDomains.flatMap((domain) => {
      const molecule = rnaComplexProps[domain.rnaComplexIndex]?.rnaMoleculeProps[domain.rnaMoleculeName];
      if (molecule === undefined) return [];
      const displayed = Object.keys(molecule.nucleotideProps).map(Number).map(i => i + molecule.firstNucleotideIndex);
      if (displayed.length === 0) return [];
      const minimum = Math.min(...displayed);
      const maximum = Math.max(...displayed);
      return [{
        ...domain,
        start : Math.max(minimum, Math.min(maximum, domain.start)),
        stop : Math.max(minimum, Math.min(maximum, domain.stop))
      }];
    });
    if (validDomains.length === 0) {
      const domain = createDomain();
      if (domain !== undefined) updateOptions({ customDomains : [domain] });
    } else if (JSON.stringify(validDomains) !== JSON.stringify(options.customDomains)) {
      updateOptions({ customDomains : validDomains });
    }
  }, [createDomain, options.customDomains, rnaComplexProps, updateOptions]);
  const controlStyle : React.CSSProperties = {
    height : 30,
    minWidth : 0,
    width : '100%',
    boxSizing : 'border-box',
    border : `1px solid ${theme.colors.border}`,
    borderRadius : theme.borderRadius.md,
    background : theme.colors.surface,
    color : theme.colors.text,
    padding : '4px 8px',
    fontSize : theme.typography.fontSize.sm
  };
  return <div style={{ display : 'flex', flexDirection : 'column', gap : 7 }}>
    {options.customDomains.map((domain, index) => {
      const complex = rnaComplexProps[domain.rnaComplexIndex];
      const molecules = Object.entries(complex?.rnaMoleculeProps ?? {});
      const molecule = complex?.rnaMoleculeProps[domain.rnaMoleculeName];
      const displayed = Object.keys(molecule?.nucleotideProps ?? {}).map(Number).map(i => i + (molecule?.firstNucleotideIndex ?? 0));
      const minimum = displayed.length ? Math.min(...displayed) : 0;
      const maximum = displayed.length ? Math.max(...displayed) : 0;
      const clamp = (value: number) => Math.max(minimum, Math.min(maximum, Number.isFinite(value) ? value : minimum));
      return <div key={index} style={{ display : 'flex', flexDirection : 'column', gap : 6, padding : '8px', border : `1px solid ${theme.colors.border}`, borderRadius : theme.borderRadius.md, background : theme.colors.backgroundSecondary }}>
        {complexes.length > 1 && <label style={{ display : 'grid', gridTemplateColumns : '70px minmax(0, 1fr)', alignItems : 'center', gap : 8, fontSize : theme.typography.fontSize.xs, color : theme.colors.textSecondary }}><span>Complex</span><select aria-label="RNA complex" value={domain.rnaComplexIndex} onChange={e => { const replacement = createDomain(Number(e.target.value)); if (replacement !== undefined) update(index, replacement); }} style={controlStyle}>{complexes.map(([i, c]) => <option key={i} value={i}>{c.name}</option>)}</select></label>}
        {molecules.length > 1 && <label style={{ display : 'grid', gridTemplateColumns : '70px minmax(0, 1fr)', alignItems : 'center', gap : 8, fontSize : theme.typography.fontSize.xs, color : theme.colors.textSecondary }}><span>Molecule</span><select aria-label="RNA molecule" value={domain.rnaMoleculeName} onChange={e => { const replacement = createDomain(domain.rnaComplexIndex, e.target.value); if (replacement !== undefined) update(index, replacement); }} style={controlStyle}>{molecules.map(([name]) => <option key={name}>{name}</option>)}</select></label>}
        <div style={{ display : 'grid', gridTemplateColumns : 'auto minmax(0, 1fr) auto minmax(0, 1fr) auto', alignItems : 'center', gap : 6 }}>
          <label htmlFor={`custom-start-${index}`} style={{ fontSize : theme.typography.fontSize.xs, color : theme.colors.textSecondary }}>Start</label>
          <input id={`custom-start-${index}`} aria-label={`Start ${index + 1}`} type="number" min={minimum} max={maximum} value={clamp(domain.start)} onChange={e => update(index, { start: clamp(Number(e.target.value)) })} style={controlStyle}/>
          <label htmlFor={`custom-stop-${index}`} style={{ fontSize : theme.typography.fontSize.xs, color : theme.colors.textSecondary }}>Stop</label>
          <input id={`custom-stop-${index}`} aria-label={`Stop ${index + 1}`} type="number" min={minimum} max={maximum} value={clamp(domain.stop)} onChange={e => update(index, { stop: clamp(Number(e.target.value)) })} style={controlStyle}/>
          {options.customDomains.length > 1
            ? <button aria-label={`Remove range ${index + 1}`} onClick={() => updateOptions({ customDomains: options.customDomains.filter((_, i) => i !== index) })} style={{ display : 'flex', alignItems : 'center', justifyContent : 'center', width : 28, height : 28, color : theme.colors.error, background : 'transparent', border : 0, cursor : 'pointer' }}><Trash2 size={15}/></button>
            : <span style={{ width : 28 }}/>
          }
        </div>
      </div>;
    })}
    <button aria-label="Add range" onClick={add} disabled={complexes.length === 0} style={{ display : 'flex', alignItems : 'center', justifyContent : 'center', gap : 6, width : '100%', height : 34, color : complexes.length === 0 ? theme.colors.textMuted : theme.colors.primary, background : theme.colors.backgroundSecondary, border : `1px solid ${theme.colors.border}`, borderRadius : theme.borderRadius.md, cursor : complexes.length === 0 ? 'not-allowed' : 'pointer', fontSize : theme.typography.fontSize.sm, fontWeight : 600 }}><Plus size={15}/><span>Add range</span></button>
  </div>;
};

export const SelectionPanel: React.FC<SelectionPanelProps> = ({
  constraint,
  onConstraintChange,
  rnaComplexProps = {},
}) => {
  const { theme } = useTheme();
  const [activeGroup, setActiveGroup] = React.useState<string>('Primitives');
  const { complexCount, moleculeCount, colorCount } = getSceneConstraintCounts(rnaComplexProps);
  const availableConstraintGroups : Record<string, Array<InteractionConstraint.Enum>> = {
    ...constraintGroups,
    Units : constraintGroups.Units.filter((candidate) => {
      if (candidate === InteractionConstraint.Enum.RNA_MOLECULE) return moleculeCount > 1;
      if (candidate === InteractionConstraint.Enum.RNA_COMPLEX) return complexCount > 1;
      if (candidate === InteractionConstraint.Enum.SINGLE_COLOR) return colorCount > 1;
      return true;
    })
  };
  React.useEffect(() => {
    const hiddenUnitConstraint =
      (constraint === InteractionConstraint.Enum.RNA_MOLECULE && moleculeCount <= 1) ||
      (constraint === InteractionConstraint.Enum.RNA_COMPLEX && complexCount <= 1) ||
      (constraint === InteractionConstraint.Enum.SINGLE_COLOR && colorCount <= 1);
    if (hiddenUnitConstraint) onConstraintChange?.(InteractionConstraint.Enum.ENTIRE_SCENE);
  }, [colorCount, complexCount, constraint, moleculeCount, onConstraintChange]);

  return (
    <PanelContainer title="Constraints modes" borderRadius={8}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        <div>
          <div style={{ 
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            gap: '2px',
            marginBottom: '4px',
            borderBottom: `1px solid ${theme.colors.border}`,
          }}>
            {Object.keys(availableConstraintGroups).map((groupName) => (
              <button
                key={groupName}
                onClick={() => {
                  setActiveGroup(groupName);
                  if (groupName === 'Scope') onConstraintChange?.(InteractionConstraint.Enum.CUSTOM_RANGE);
                }}
                style={{
                  height: theme.buttonSizes.md.height,
                  padding: '4px 2px',
                  border: 'none',
                  background: activeGroup === groupName ? theme.colors.primary : 'transparent',
                  color: activeGroup === groupName ? theme.colors.textInverse : theme.colors.text,
                  borderRadius: `${theme.borderRadius.md} ${theme.borderRadius.md} 0 0`,
                  fontSize: theme.buttonSizes.sm.fontSize,
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                {groupName}
              </button>
            ))}
          </div>
          
          {activeGroup !== 'Scope' && <div style={{
            display: 'flex', 
            flexWrap: 'wrap', 
            gap: '6px',
            minHeight: '30px',
            alignItems: 'flex-start',
            alignContent: 'flex-start',
          }}>
            {availableConstraintGroups[activeGroup]?.map((c) => (
              <ConstraintChip
                key={c}
                constraint={c}
                isActive={constraint === c}
                onClick={() => onConstraintChange?.(c)}
                disabled={!onConstraintChange}
              />
            ))}
          </div>}
          {activeGroup === 'Scope' && <CustomDomainsEditor rnaComplexProps={rnaComplexProps}/>}
        </div>
      </div>
    </PanelContainer>
  );
};
